import { Component, createSignal, Show, Suspense, JSX } from "solid-js";
import { createAsync } from "@solidjs/router";
import { Icon } from "solid-heroicons";
import { envelope } from "solid-heroicons/solid";
import { bellAlert } from "solid-heroicons/outline";
import { StockSubscription } from "./StockSubscription";
import { Modal } from "../utility";
import { getSubscription } from "~/services/inventory-subscriptions/useSubscription";
import { checkError } from "~/services/roma-api/checkError";
import { APIError } from "~/services/roma-api/errors";

type TriggerProps = {
  open: () => void;
  isSubscribed: () => boolean;
};

export const StockSubscribeButton: Component<{
  inventory: any;
  product: any;

  /** Optional custom trigger UI */
  trigger?: (p: TriggerProps) => JSX.Element;

  /** Called ONLY when the modal closes after a successful subscribe/update */
  onCloseAfterSubscribe?: () => void;
}> = (props) => {
  const [isSubscribed, setIsSubscribed] = createSignal(false);
  const [showModal, setShowModal] = createSignal(false);

  // tracks whether *this modal session* completed a subscribe/update
  const [didSubscribe, setDidSubscribe] = createSignal(false);

  const activeSubscription = createAsync(async () => {
    try {
      const data = await getSubscription(props.product.SKU);
      if (data) {
        setIsSubscribed(true);
        return data;
      }
    } catch (error) {
      setIsSubscribed(false);
      const err = checkError(error);
      if (
        err instanceof APIError &&
        (err.code === "NO_SUBSCRIPTION" || err.statusCode === 404)
      ) {
        return;
      }
      throw error;
    }
  });

  const open = () => {
    setDidSubscribe(false); // reset per open
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);

    if (didSubscribe()) {
      props.onCloseAfterSubscribe?.();
    }
  };

  return (
    <>
      {props.trigger ? (
        props.trigger({ open, isSubscribed })
      ) : (
        <button
          aria-label={
            isSubscribed()
              ? "Manage your current subscription"
              : "Subscribe to stock notifications"
          }
          class="mt-2 text-xs border border-roma-blue rounded-full text-roma-blue w-full relative"
          onClick={open}
        >
          <div class="flex justify-center items-center p-1">
            <Icon
              path={isSubscribed() ? bellAlert : envelope}
              class="w-4 h-4 mr-2"
            />
            <span>
              {isSubscribed()
                ? "Manage your current subscription"
                : "Subscribe to stock notifications"}
            </span>
          </div>
          <Show when={isSubscribed()}>
            <div class="w-3 rounded-full aspect-square bg-blue-400 absolute top-1.5 left-1.5 animate-ping" />
            <div class="w-3 rounded-full aspect-square bg-roma-blue absolute top-1.5 left-1.5 " />
          </Show>
        </button>
      )}

      <Suspense>
        <Modal open={showModal} onClose={closeModal}>
          <StockSubscription
            inventory={props.inventory}
            product={props.product}
            activeSubscription={activeSubscription()}
            closeModal={closeModal}
            onSubscribed={() => setDidSubscribe(true)} // <-- add this
          />
        </Modal>
      </Suspense>
    </>
  );
};
